// D2-1 — WHAT THE ACCOUNT MENU SAYS ABOUT WHAT SHE IS ON, INCLUDING WHEN IT IS A GIFT.
//
// Pulled out of App.tsx by D2 for the reason roadmap 7 keeps finding: this block had to learn a
// third state (gifted, beside paying and free) and there was no instrument on it at all — every
// assertion about the members menu was a regex over an 16,000-line TSX file. It takes props, holds
// no state and calls no hook, so `scripts/d2-grants.mjs` CALLS it and reads the tree back. The
// extraction also paid for the lines D2 added to App.tsx, which is the ratchet working rather than
// merely being obeyed.
//
// THE GIFT-TRUTH RULE, and it is the whole point of the component: a member holding a comp is never
// shown a monthly price, never told a subscription "ends" on a date she is not being billed
// against, and never left guessing when the gift runs out. What she IS shown is the tier, the
// through-date and the plain fact that nothing is charged. All of that is decided on the SERVER by
// `giftTruth` in src/accessGrants.js; this renders the answer and invents none of it.

// R212 — **AND THE THIRD STATE WAS ALREADY ON THE WIRE AND READ BY NOBODY.** The whole reasoning is
// in `src/lib/membership-line.ts`; the short version is that `/api/membership/status` has carried
// `frozen` and `frozenTier` since L15, under a server comment claiming this menu says what happened,
// while `SubStatus` declared neither and a frozen year-long Vet read `Free` here with nothing beside
// it. The decision lives in that module rather than in a ternary in this markup, so a gate RUNS it
// over payloads this route can produce and several it cannot (R129/R135) — and so the three states
// can never paint at once.
import { membershipLine, accountStateAttr } from "../lib/membership-line";
import { t } from "../strings.js";
// ITEM G3 — the store app never shows a price (R3, P2): her plan's name only.
import { APP_MODE } from "../lib/appMode";

export type SubStatus = {
  tierLabel?: string;
  tier?: string;
  status?: string;
  priceText?: string;
  cancelAtPeriodEnd?: boolean;
  currentPeriodEnd?: string | null;
  canManageBilling?: boolean;
  // P2 (her item 9 choice): the soft backup-card prompt (server/billing-backup.js backupAdvice).
  backupCardSuggested?: boolean;
  purchases?: string[];
  lalaTurnsPerDay?: number | null;
  // Present only while a grant is what governs this member's access.
  gift?: { note: string; endsAt: string | null; source: string } | null;
  // L15/R212 — **TWO FACTS, AND ONLY ONE OF THEM IS A TIER (R18).** `tier`/`tierLabel` are what she
  // may open right now, which for a frozen member is `free`; these are what she is one tap from
  // reopening. **A FIELD THE SERVER SENDS AND THIS TYPE DOES NOT DECLARE IS EXACTLY THE DEFECT THIS
  // ROUND IS ABOUT** — it renders perfectly and is invisible from every direction.
  frozen?: boolean;
  frozenTier?: string | null;
  frozenTierLabel?: string | null;
  /** Her signup order: this house cannot reach her by email, and the account menu says so. */
  emailUndeliverable?: boolean;
  /** >>> HER CORRECTION (2026-08-19): the reading she clicked to arrive, or "" for an organic
   *  signup. It rides THIS payload because `membershipKnown` is derived from the same answer, and
   *  My Room needs both facts to agree — a field the server sends and this type does not declare is
   *  the R212 defect one line up, which renders perfectly and is invisible from every direction. */
  originReading?: string;
};

// ITEM 14(b) — `language` is OPTIONAL and defaults to English, so every existing call site and every
// rendered check that points at this block is byte-identical to before. **WHAT IS DELIBERATELY NOT
// TRANSLATED HERE IS EVERY FIELD THE SERVER COMPOSED** — `tierLabel`, `priceText` and a gift's own
// note are sentences written on the server (part (d)), and re-translating a server sentence on the
// client would be a second answer key for copy the ledger already recorded.
export function AccountSubscription({ status, isMember, language = "en" }: { status: SubStatus | null; isMember: boolean; language?: string }) {
  const gift = status?.gift || null;
  const line = membershipLine(status);
  return (
    <div className="account-menu__sub" data-account-state={accountStateAttr(line, status?.tier)}>
      <span className="account-menu__sublabel">{t(language, "sub.heading")}</span>
      <strong className="account-menu__tier">
        {/* R212 — **WHILE SHE IS PAUSED THE BOLD LINE NAMES THE TIER SHE IS COMING BACK TO**, which
            is the gift's own shape one state over: when something other than a plain payment
            governs, the label is the thing she holds and a note under it says what is happening.
            `tierLabel` is `Free` for her, because that is the label of what is OPEN, and printing it
            here alone is R87's reported defect word for word. It falls back to `tierLabel` when the
            house cannot NAME the held tier, so the fallback is today's behaviour and never something
            worse. */}
        {line.heldLabel || status?.tierLabel || (isMember ? t(language, "account.pill.member") : t(language, "sub.free"))}
        {/* The price line is suppressed for a gift by the SERVER (it sends priceText: ""), so this
            renders whatever it is told and cannot invent a charge she is not paying. It is empty for
            a paused member for the same reason and by the same mechanism — `tierByKey("free")` is
            null — so nothing here claims a charge that is not being taken. */}
        {!APP_MODE && status?.priceText ? <span> {t(language, "sub.perMonth", { price: status.priceText })}</span> : null}
      </strong>

      {line.state === "gift" && gift ? <span className="account-menu__note account-menu__note--gift">{gift.note}</span> : null}

      {/* R212 — the pause, said in one line, with Manage billing two rows below it. */}
      {line.state === "paused" ? (
        <span className="account-menu__note account-menu__note--paused">{t(language, "sub.paused")}</span>
      ) : null}

      {/* A cancelled member is NOT gone: she keeps what she paid for until the period ends, and
          saying so is the difference between a service and a trapdoor. Suppressed under a gift,
          which has no billing period to end. */}
      {!gift && status?.cancelAtPeriodEnd && status?.currentPeriodEnd ? (
        <span className="account-menu__note">
          {t(language, "sub.endsOn", { date: new Date(status.currentPeriodEnd).toLocaleDateString() })}
        </span>
      ) : null}

      {status?.purchases?.length ? (
        <span className="account-menu__note">{t(language, "sub.spiritKeeping")}</span>
      ) : null}
    </div>
  );
}
